import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Tooltip, useMap, GeoJSON, LayersControl, LayerGroup } from 'react-leaflet';
import L, { DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Same icon style as MapView - blue filled for owned, orange for shared, green ring for public
const createStationIcon = (type: 'owner' | 'shared' | 'public') => {
  const colors: Record<string, { fill: string; border: string; hollow: boolean }> = {
    owner:  { fill: '#2196f3', border: 'white',   hollow: false },
    shared: { fill: '#ff9800', border: 'white',   hollow: false },
    public: { fill: 'white',   border: '#558b2f', hollow: true  },
  };
  const c = colors[type];

  return new DivIcon({
    html: `
      <div style="
        background-color: ${c.fill};
        width: 25px;
        height: 25px;
        border-radius: 50%;
        border: ${c.hollow ? '4px' : '3px'} solid ${c.border};
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        cursor: pointer;
      ">
        <div style="
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 8px solid ${c.hollow ? c.border : c.fill};
        "></div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [25, 35],
    iconAnchor: [12, 35],
  });
};

// Grey/disabled version for already-selected stations
const disabledIcon = new DivIcon({
  html: `
    <div style="
      background-color: #9e9e9e;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      border: 3px solid #bdbdbd;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      opacity: 0.5;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    ">
      <div style="
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 8px solid #9e9e9e;
      "></div>
    </div>
  `,
  className: 'custom-marker',
  iconSize: [25, 35],
  iconAnchor: [12, 35],
});

interface Station {
  id?: number;
  uuid?: string;
  name: string;
  status: string;
  is_owner?: boolean;
  is_shared?: boolean;
  available_metrics?: string[];
  location?: {
    latitude: number;
    longitude: number;
  };
}

interface StationPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (station: Station) => void;
  stations: Station[];
  selectedStationIds: string[];
}

const StationPickerDialog: React.FC<StationPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  stations,
  selectedStationIds
}) => {
  const [giscoBoundaries, setGiscoBoundaries] = useState<any>(null);
  const [giscoLabelPoints, setGiscoLabelPoints] = useState<any>(null);

  useEffect(() => {
    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_RG_20M_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoBoundaries(data))
      .catch(err => console.warn('Failed to load GISCO boundaries:', err));

    fetch('https://gisco-services.ec.europa.eu/distribution/v2/countries/geojson/CNTR_LB_2024_4326.geojson')
      .then(res => res.json())
      .then(data => setGiscoLabelPoints(data))
      .catch(err => console.warn('Failed to load GISCO label points:', err));
  }, []);

  const giscoBoundaryStyle = {
    color: '#2c3e50', weight: 2, opacity: 0.8, fillColor: 'transparent', fillOpacity: 0,
  };

  const toponymLabels = React.useMemo(() => {
    if (giscoLabelPoints) {
      return giscoLabelPoints.features
        .filter((f: any) => f.properties?.NAME_ENGL && f.geometry?.coordinates)
        .map((feature: any) => ({
          name: feature.properties.NAME_ENGL,
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0],
        }));
    }
    return [];
  }, [giscoLabelPoints]);

  const [showGiscoBoundaries, setShowGiscoBoundaries] = useState(true);
  const [showGiscoToponyms, setShowGiscoToponyms] = useState(false);
  const [showOsm, setShowOsm] = useState(true);

  const GiscoLayerEvents = () => {
    const map = useMap();
    useEffect(() => {
      const onAdd = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(true);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(true);
        if (e.name === 'OpenStreetMap') setShowOsm(true);
      };
      const onRemove = (e: any) => {
        if (e.name === 'GISCO Boundaries') setShowGiscoBoundaries(false);
        if (e.name === 'Official EC Toponyms') setShowGiscoToponyms(false);
        if (e.name === 'OpenStreetMap') setShowOsm(false);
      };
      map.on('overlayadd', onAdd);
      map.on('overlayremove', onRemove);
      return () => { map.off('overlayadd', onAdd); map.off('overlayremove', onRemove); };
    }, [map]);
    return null;
  };

  const GiscoBaseOpacity = () => {
    const map = useMap();
    useEffect(() => {
      const giscoActive = showGiscoBoundaries || showGiscoToponyms;
      map.eachLayer((l: any) => {
        if (l.setOpacity && l._url && (l._url.includes('openstreetmap') || l._url.includes('cartocdn'))) {
          l.setOpacity(giscoActive ? 0.5 : 1);
        }
      });
    }, [showGiscoBoundaries, showGiscoToponyms, map]);
    return null;
  };

  const GiscoDisclaimer = () => {
    const map = useMap();
    useEffect(() => {
      const disclaimer = new L.Control({ position: 'bottomright' });
      disclaimer.onAdd = () => {
        const div = L.DomUtil.create('div', 'gisco-disclaimer');
        div.style.cssText = 'background:rgba(255,255,255,0.85);padding:4px 8px;font-size:10px;max-width:420px;line-height:1.3;color:#333;border-radius:3px;pointer-events:none;margin-right:10px;margin-bottom:5px;';
        div.innerHTML = 'The designations employed and the presentation of material on this map do not imply the expression of any opinion whatsoever concerning the legal status of any country, territory, city or area or of its authorities, or concerning the delimitation of its frontiers or boundaries.';
        return div;
      };
      disclaimer.addTo(map);
      return () => { disclaimer.remove(); };
    }, [map]);
    return null;
  };

  const getStationId = (station: Station): string => {
    return station.uuid || String(station.id) || '';
  };

  // Filter stations that have valid coordinates
  const stationsWithLocation = stations.filter(
    s => s.location && s.location.latitude && s.location.longitude
  );

  const handleMarkerClick = (station: Station) => {
    const stationId = getStationId(station);
    if (selectedStationIds.includes(stationId)) {
      return;
    }
    onSelect(station);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Select Station from Map
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {stationsWithLocation.length === 0 ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 450,
            color: 'text.secondary'
          }}>
            <Typography>No stations with location data available</Typography>
          </Box>
        ) : (
          <Box sx={{ height: 450, width: '100%', position: 'relative' }}>
            <MapContainer
              center={[39.07, 21.82]}
              zoom={6}
              style={{ height: '100%', width: '100%' }}
              attributionControl={false}
            >
              <GiscoLayerEvents />
              <GiscoBaseOpacity />
              <LayersControl position="topright">
                <LayersControl.Overlay checked name="OpenStreetMap">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    subdomains="abc"
                    maxZoom={19}
                  />
                </LayersControl.Overlay>
                <LayersControl.Overlay checked name="GISCO Boundaries">
                  <LayerGroup>
                    {giscoBoundaries && (
                      <GeoJSON data={giscoBoundaries} style={giscoBoundaryStyle} />
                    )}
                  </LayerGroup>
                </LayersControl.Overlay>
                <LayersControl.Overlay name="Official EC Toponyms">
                  <LayerGroup>
                    {toponymLabels.map((t: any) => (
                      <Marker key={t.name} position={[t.lat, t.lng]} interactive={false}
                        icon={new DivIcon({
                          className: 'gisco-toponym',
                          html: `<span style="font-size:14px;font-weight:700;color:#1a237e;text-shadow:-1px -1px 0 #fff,1px -1px 0 #fff,-1px 1px 0 #fff,1px 1px 0 #fff,0 -1px 0 #fff,0 1px 0 #fff,-1px 0 0 #fff,1px 0 0 #fff;white-space:nowrap;pointer-events:none;">${t.name}</span>`,
                          iconSize: [0, 0], iconAnchor: [0, 0],
                        })}
                      />
                    ))}
                  </LayerGroup>
                </LayersControl.Overlay>
              </LayersControl>
              <GiscoDisclaimer />
              {stationsWithLocation.map((station) => {
                const stationId = getStationId(station);
                const isSelected = selectedStationIds.includes(stationId);
                const stationType = station.is_owner ? 'owner' : station.is_shared ? 'shared' : 'public';
                const icon = isSelected
                  ? disabledIcon
                  : createStationIcon(stationType);

                return (
                  <Marker
                    key={stationId}
                    position={[station.location!.latitude, station.location!.longitude]}
                    icon={icon}
                    eventHandlers={{
                      click: () => handleMarkerClick(station)
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -35]}>
                      {station.name}{isSelected ? ' (already selected)' : ''}
                    </Tooltip>
                  </Marker>
                );
              })}
            </MapContainer>

            {/* Legend - same style as MapView */}
            <Box sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              boxShadow: 2,
              minWidth: 150,
              zIndex: 1000
            }}>
              <Typography variant="subtitle2" gutterBottom>
                Stations
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196f3', mr: 1 }} />
                <Typography variant="caption">My Stations</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800', mr: 1 }} />
                <Typography variant="caption">Shared with me</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'white', border: '3px solid #558b2f', mr: 1, boxSizing: 'border-box' }} />
                <Typography variant="caption">Public</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9e9e9e', opacity: 0.5, mr: 1 }} />
                <Typography variant="caption">Already Selected</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          {stationsWithLocation.length} station{stationsWithLocation.length !== 1 ? 's' : ''} on map
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StationPickerDialog;
